import { Button, Flex, Drawer, Text, Tabs, Box } from '@chakra-ui/react';
import { useForm, FormProvider, useWatch } from 'react-hook-form';
import { ConfirmModal, createDataCollectionContext, FormInput, Loading, toaster, useValidatedSorting, type ConfirmModalHandle } from '@shared';
import { useCreateGroup, useDeleteGroup, useGetSingleGroup, useUpdateGroup } from '../../../../api/hooks/useGroups';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import { IoBookOutline } from 'react-icons/io5';
import contactsService from '../../../../api/services/contactsService';
import { useDrawer, useGetAllCustomFields } from '@admin';
import ContactDrawer from '../contacts/drawer/ContactDrawer';
import { ExportModal } from '../contacts/BrowseContacts';

const columnHelper = createColumnHelper<Contact>();

type Props = {
	onCreated?: (data: Group) => void;
	groupID?: number;
	currentAccount: Account;
};

type GroupForm = {
	name: string;
};

export const GroupDrawer = ({ onCreated, currentAccount, groupID }: Props) => {
	const { t } = useTranslation();
	const createGroup = useCreateGroup();
	const updateGroup = useUpdateGroup();

	const { openDrawer, closeDrawer } = useDrawer();
	const { useDataCollection, DataCollectionContextProvider, DataCollectionTable, DataCollectionTableFooter, DataCollectionTableHeader } = useMemo(
		() => createDataCollectionContext<Contact>(),
		[],
	);
	const queryClient = useQueryClient();
	const { data: customFieldData } = useGetAllCustomFields();

	const { data: group, isFetching: isFetchingGroup } = useGetSingleGroup(groupID as number, {
		isEnabled: !!groupID,
	});

	const methods = useForm<GroupForm>({
		defaultValues: {
			...group,
		},
	});

	const {
		reset,
		control,
		formState: { dirtyFields },
	} = methods;

	useEffect(() => {
		if (group) {
			reset({
				...group,
			});
		}
	}, [group, reset]);

	function handleUpdateGroup(data: GroupForm): void {
		if (!group) {
			return;
		}
		const payload: GroupUpdateRequest = {
			groupID: group.groupID,
		};

		(Object.keys(dirtyFields) as Array<keyof GroupForm>).forEach(key => {
			const value = data[key];

			if (value !== undefined) {
				payload[key] = value;
			}
		});

		updateGroup.mutate(payload, {
			onSuccess: response => {
				toaster.create({ type: 'success', title: t('general.saved') });
				queryClient.invalidateQueries({ queryKey: ['groups-with-contact-counts'] });
				queryClient.invalidateQueries({ queryKey: ['groups'] });
				queryClient.setQueryData(['group', response.groupID], response);
				closeDrawer('groupDrawer');
			},
		});
	}

	const onSuccess = (data: Group): void => {
		if (onCreated) onCreated(data);
		toaster.create({ type: 'success', title: t('contacts.group_added') });
		queryClient.invalidateQueries({ queryKey: ['groups-with-contact-counts'] });
		queryClient.invalidateQueries({ queryKey: ['groups'] });
		closeDrawer('groupDrawer');
	};

	const onSave = (data: GroupForm): void => {
		if (groupID) {
			handleUpdateGroup(data);
		} else {
			createGroup.mutate(
				{ ...data },
				{
					onSuccess,
				},
			);
		}
	};

	const waitForUserResponseOnRemoveGroup = useRef<ConfirmModalHandle>(null);

	const { mutate: deleteGroup } = useDeleteGroup();

	async function handleRemove(): Promise<void> {
		const response = await waitForUserResponseOnRemoveGroup.current?.open({
			title: t('general.remove_group'),
			content: t('general.really_remove_group'),
			confirmButtonText: t('general.remove'),
			confirmButtonVariant: 'subtle',
		});

		if (!groupID) {
			return;
		}

		if (response === true) {
			deleteGroup(groupID, {
				onSuccess: () => {
					toaster.create({
						type: 'success',
						title: t('general.removed'),
					});
					queryClient.invalidateQueries({ queryKey: ['groups-with-contact-counts'] });
					queryClient.invalidateQueries({ queryKey: ['groups'] });
					closeDrawer('groupDrawer');
				},
			});
		}
	}

	const columns = [
		columnHelper.accessor('phone', {
			header: t('general.phone'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				return <Text userSelect={'text'}>{row.original.phone}</Text>;
			},
		}),

		columnHelper.accessor('firstName', {
			header: t('general.first_name'),
			sortingFn: 'basic',
		}),
		columnHelper.accessor('lastName', {
			header: t('general.last_name'),
			sortingFn: 'basic',
		}),
		columnHelper.accessor('email', {
			header: t('general.email'),
			sortingFn: 'basic',
		}),
	];

	useValidatedSorting('contacts', columns);

	const name = useWatch({ control, name: 'name' });
	const selectColumns = useRef<ConfirmModalHandle>(null);

	function openExportModal(): void {
		if (!customFieldData) {
			return;
		}

		selectColumns.current?.open({
			title: t('general.export'),
			content: <ExportModal ref={selectColumns} currentAccount={currentAccount} customFields={customFieldData?.data.customFields} useDataCollection={useDataCollection} />,
			isConfirmButtonVisible: false,
			isBackButtonVisible: false,
		});
	}

	if (isFetchingGroup) {
		return <Loading />;
	}

	return (
		<FormProvider {...methods}>
			<Drawer.Header>
				<Drawer.Title wordBreak={'break-word'}>{group ? name : t('contacts.add_group')}</Drawer.Title>
			</Drawer.Header>
			<Tabs.Root
				flexDir={'column'}
				defaultValue={'basicDetails'}
				variant={'line'}
				size="sm"
				colorPalette={'brand.primary'}
				width={'100%'}
				lazyMount
				unmountOnExit
				display={'flex'}
				flexDirection={'column'}
				height={'100%'}
				overflow="auto"
			>
				<Tabs.List padding={0} paddingLeft={4} flexWrap={'wrap'}>
					<Tabs.Trigger value="basicDetails" fontWeight={500}>
						{t('general.basic_details')}
					</Tabs.Trigger>
					{groupID ? (
						<Tabs.Trigger value="contacts" fontWeight={500}>
							{t('general.contacts')}
						</Tabs.Trigger>
					) : null}
				</Tabs.List>
				<Tabs.Content value="basicDetails" display={'flex'} flexDir={'column'} flex="1">
					<Drawer.Body flex="1">
						<Flex id="addGroup" height="100%" as="form" onSubmit={methods.handleSubmit(onSave)} gap={4} flexDir="column">
							<FormInput isRequired label={t('general.name')} name="name" maxLength={100} />
						</Flex>
					</Drawer.Body>
					<Drawer.Footer>
						{groupID ? (
							<>
								<ConfirmModal ref={waitForUserResponseOnRemoveGroup} />
								<Button marginRight={'auto'} data-cy="removeGroupButton" variant="subtle" width={'fit-content'} onClick={handleRemove}>
									{t('general.remove')}
								</Button>
							</>
						) : null}
						<Button data-cy="groupDrawerCancelButton" disabled={createGroup.isPending} onClick={() => closeDrawer('groupDrawer')} variant="outline">
							{t('general.cancel')}
						</Button>
						<Button loading={createGroup.isPending} form="addGroup" type="submit">
							{t('general.save')}
						</Button>
					</Drawer.Footer>
				</Tabs.Content>

				<Tabs.Content display="flex" value="contacts" flex={1} overflow={'auto'}>
					<Drawer.Body display="flex" flexDirection="column" padding={5} gap={5} overflow={'auto'}>
						<DataCollectionContextProvider
							useParamsToSort={false}
							customQueryKey={`contacts-group-${groupID}`}
							dataKey="contacts"
							keyAttribute="contactID"
							emptyDataText={t('general.no_contacts_found')}
							emptyDataIcon={IoBookOutline}
							fetchFn={(...args) => contactsService.getAllResources(...args)}
							queryParams={{ relatedFilters: `contactGroups:groupID:eq:${groupID}` }}
							initialItemsPerPage={25}
							columns={columns.filter(column => column !== undefined)}
							defaultHiddenColumns={['postalCode']}
							isServerSide={true}
							isSelectable={false}
							defaultSort={{ id: 'timeCreated', desc: true }}
							onClickRow={row => {
								openDrawer({
									id: 'contactDrawer',
									content: <ContactDrawer closeDrawer={() => closeDrawer('contactDrawer')} contactID={row.original.contactID} />,
								});
							}}
						>
							<Flex gap={2} flexWrap="wrap" flexShrink={0} ml="auto">
								<Button onClick={openExportModal}>{t('general.export')}</Button>

								<DataCollectionTableHeader />
							</Flex>
							<Box flex={1} overflow="auto" minH={0}>
								<DataCollectionTable />
							</Box>
							<Flex gap={2} flexShrink={0}>
								<DataCollectionTableFooter />
								<ConfirmModal ref={selectColumns} />
							</Flex>
						</DataCollectionContextProvider>
					</Drawer.Body>
				</Tabs.Content>
			</Tabs.Root>
		</FormProvider>
	);
};
